<script>
import findIndex from 'lodash/findIndex';
import { DateTime } from 'luxon';
import NewsRecordModel from '@models/NewsRecord';
import NewsModal from '@views/news/modal';
import BlockingContainer from '@components/BlockingContainer';
import HeaderComponent from '@components/HeaderComponent';
import FileDownloadLink from '@components/FileDownloadLink';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Layout from '@layouts/main';

import 'viewerjs/dist/viewer.css';
import Viewer from 'v-viewer';
import Vue from 'vue';
Vue.use(Viewer);

export default {
  name: 'NewsHomePage',
  page() {
    return {
      title: this.$t('titles.home'),
    };
  },
  components: {
    NewsModal,
    FontAwesomeIcon,
    BlockingContainer,
    HeaderComponent,
    FileDownloadLink,
    Layout,
  },
  data() {
    return {
      selectedNewsRecord: null,
      modalShow: false,
      records: [],
      options: {
        url: 'data-source',
      },
      sortBy: 'id',
      sortDesc: false,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'created_at', label: this.$t('table_labels.date'), sortable: false, formatter: this.formatDate },
        { key: 'title', label: this.$t('table_labels.topic'), sortable: false },
        { key: 'attachment', label: this.$t('table_labels.attachment'), sortable: false },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
  },
  methods: {
    itemsProvider(ctx) {
      return NewsRecordModel.orderBy(this.sortDir + ctx.sortBy)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          return items || [];
        })
        .catch(() => []);
    },
    submit(record) {
      const key = findIndex(this.records, { id: record.id });
      if (key !== -1) {
        this.$set(this.records, key, record);
      } else {
        if (this.records.length === 0) {
          this.$nextTick(() => {
            this.$refs.infiniteLoadingRecord.$emit('$InfiniteLoading:reset');
          });
        } else {
          this.records.unshift(record);
        }
      }
      this.close();
    },
    show(id) {
      if (id) this.selectedNewsRecord = id;
      this.modalShow = true;
    },
    close() {
      this.modalShow = false;
      this.selectedNewsRecord = null;
    },
    async deleteEntity(id, key) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.news_record') }))) {
        this.$wait.start(`record ${key} processing`);
        const record = new NewsRecordModel({ id });
        await record.delete();
        this.$wait.end(`record ${key} processing`);
        this.records.splice(key, 1);
      }
    },
    formatDate(value) {
      return DateTime.fromSQL(value).toFormat('dd.MM.yyyy');
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent :title="$t('titles.news')" :button-text="$t('titles.create_news_record')" @button-pressed="$router.push({ name: 'news.create' })"></HeaderComponent>
      </b-col>
    </b-row>
     <div id="news-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('news processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <!-- Custom icons -->
            <template>
              <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
              <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
              <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
            </template>
          </template>
        </template>
        <template v-slot:cell(client)="row">
          <div v-can:doAccountingOrSuperAdministrate :class="$style.statusContainer">
            <div v-show="!$wait.is(`statusLoader${row.item.id}`)">
              <b-form-checkbox v-model="row.item.client" :value="1" :unchecked-value="0" @change="updateItem(row.item)"> </b-form-checkbox>
            </div>

            <FontAwesomeIcon v-show="$wait.is(`statusLoader${row.item.id}`)" :class="$style.statuLoadingIcon" :icon="['fas', 'spinner']" pulse />
          </div>
          <div v-can:doAccountingOrSuperAdministrate.not>
            <FontAwesomeIcon v-if="parseInt(row.item.client, 10)" class="text-success" :icon="['fas', 'check-circle']" />
            <FontAwesomeIcon v-else class="text-danger" :icon="['fas', 'times-circle']" />
          </div>
        </template>
        <template v-slot:cell(attachment)="row">
          <FileDownloadLink v-if="row.item.attachment" :file="row.item.attachment" :removable="false" />
          <template v-else>-</template>
        </template>
        <template v-slot:cell(actions)="row">
          <div class="d-flex">
            <b-btn class="mr-2" size="sm" variant="outline-primary" :to="{ name: 'news.edit', params: { id: row.item.id } }">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
          </div>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>
    <b-row>
      <b-col sm="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
      </b-col>
    </b-row>
    <NewsModal v-model="modalShow" :loaded-id="selectedNewsRecord" @submitted="submit" @hidden="close" />
  </Layout>
</template>
